import React, { useEffect } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Briefcase,
  Building2,
  Camera,
  Car,
  Check,
  FileText,
  Landmark,
  type LucideIcon,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import { cn } from "shared/lib/utils";
import FormAutocomplete from "shared/components/forms/FormAutocomplete";
import type { SupportedLanguage } from "shared/i18n/i18n";
import {
  localizeCity,
  type CcCity,
} from "features/courier-onboarding/services/lookupContentService";
import {
  BusinessTrack,
  CourierAreaType,
  CourierDocumentKey,
  requiredDocsByTrack,
} from "features/courier-onboarding/types";

interface Props {
  track: BusinessTrack | null;
  onTrackChange: (track: BusinessTrack) => void;
  docsReady: boolean;
  onDocsReadyChange: (ready: boolean) => void;
  cities: CcCity[];
  cityValue: string;
  onCityChange: (value: string) => void;
  cityLocked?: boolean;
  /** Area category of the selected city; hides the finance track for EXPANSION. */
  areaType: CourierAreaType;
}

const DOC_ICONS: Record<CourierDocumentKey, LucideIcon> = {
  profilePicture: Camera,
  driverLicense: Car,
  businessLicense: FileText,
  bankAccount: Landmark,
};

const DocOrderStep: React.FC<Props> = ({
  track,
  onTrackChange,
  docsReady,
  onDocsReadyChange,
  cities,
  cityValue,
  onCityChange,
  cityLocked = false,
  areaType,
}) => {
  const { t, i18n } = useTranslation();
  const reduceMotion = !!useReducedMotion();
  const uiLang: SupportedLanguage =
    i18n.language === "he" || i18n.language === "ar" ? i18n.language : "en";

  // In expansion areas only the licensed-business (Tax ID) track is available;
  // the finance-company option is cancelled.
  const financeAvailable = areaType !== "EXPANSION";

  // Reset the readiness confirmation whenever the selected track changes
  // since the required document set differs per track.
  useEffect(() => {
    onDocsReadyChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track]);

  return (
    <div className="flex flex-col gap-7">
      {/* ───────────────────────── Step 1: Area / city ───────────────────────── */}
      <section>
        <SectionEyebrow step={1} label={t("onboarding.docOrder.areaTitle")} />
        <p className="mt-2 mb-4 text-sm text-gray-600 leading-relaxed ms-9">
          {t("onboarding.docOrder.areaSubtitle")}
        </p>
        <FormAutocomplete
          label={t("onboarding.personal.city")}
          placeholder={t("onboarding.personal.selectCity") ?? ""}
          value={cityValue}
          onChange={onCityChange}
          options={cities.map((c) => ({
            value: c.nameEn,
            label: localizeCity(c, uiLang),
          }))}
          disabled={cityLocked}
          required
        />
      </section>

      {/* ───────────────────────── Step 2: Track choice ───────────────────────── */}
      {cityValue ? (
        <section>
          <SectionEyebrow
            step={2}
            label={t("onboarding.docOrder.trackTitle")}
          />
          <p className="mt-2 text-sm text-gray-600 leading-relaxed ms-9">
            {t("onboarding.docOrder.trackSubtitle")}
          </p>

          <div
            role="radiogroup"
            aria-label={String(t("onboarding.docOrder.trackTitle"))}
            className="mt-4 grid grid-cols-1 gap-3"
          >
            {financeAvailable && (
              <TrackCard
                active={track === "FINANCIAL_COMPANY"}
                onClick={() => onTrackChange("FINANCIAL_COMPANY")}
                icon={<Building2 size={20} />}
                title={t("onboarding.docOrder.tracks.financialCompany.title")}
                desc={
                  t(
                    "onboarding.docOrder.tracks.financialCompany.desc",
                  ) as string
                }
                docs={requiredDocsByTrack.FINANCIAL_COMPANY}
              />
            )}
            <TrackCard
              active={track === "LICENSED_BUSINESS"}
              onClick={() => onTrackChange("LICENSED_BUSINESS")}
              icon={<Briefcase size={20} />}
              title={t("onboarding.docOrder.tracks.licensedBusiness.title")}
              desc={
                t("onboarding.docOrder.tracks.licensedBusiness.desc") as string
              }
              docs={requiredDocsByTrack.LICENSED_BUSINESS}
            />
          </div>
        </section>
      ) : (
        <p className="text-xs text-gray-500 text-center">
          {t("onboarding.docOrder.pickAreaToContinue")}
        </p>
      )}

      {/* ──────── Readiness confirm once track picked ─────── */}
      <AnimatePresence initial={false}>
        {cityValue && track && (
          <motion.div
            key={track}
            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-5"
          >
            <DocumentsReadyConfirm
              checked={docsReady}
              onChange={onDocsReadyChange}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {cityValue && !track && (
        <p className="text-xs text-gray-500 text-center mt-1">
          {t("onboarding.docOrder.pickTrackToContinue")}
        </p>
      )}
    </div>
  );
};

/* ─────────────────────────── Section eyebrow ─────────────────────────── */

interface SectionEyebrowProps {
  step: number;
  label: string;
}

const SectionEyebrow: React.FC<SectionEyebrowProps> = ({ step, label }) => (
  <div className="flex items-center gap-3">
    <span
      aria-hidden="true"
      className="relative inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#bf0035]/10 bg-[#bf0035]/[0.06] text-xs font-extrabold text-[#bf0035]"
    >
      {step}
    </span>
    <h3 className="text-base sm:text-[17px] font-bold text-gray-900 tracking-tight">
      {label}
    </h3>
  </div>
);

/* ─────────────────────────── Track card ─────────────────────────── */

interface TrackCardProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  desc: string;
  docs: CourierDocumentKey[];
}

const TrackCard: React.FC<TrackCardProps> = ({
  active,
  onClick,
  icon,
  title,
  desc,
  docs,
}) => {
  const { t } = useTranslation();
  return (
    <motion.button
      type="button"
      onClick={onClick}
      role="radio"
      aria-checked={active}
      className={cn(
        "group relative w-full rounded-xl border p-4 text-start transition-colors sm:p-5",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0035] focus-visible:ring-offset-2",
        active
          ? "border-[#bf0035] bg-[#bf0035]/[0.025]"
          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/40",
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors",
            active
              ? "bg-[#bf0035]/[0.07] text-[#bf0035]"
              : "bg-gray-50 text-gray-600",
          )}
          aria-hidden
        >
          {icon}
        </span>

        <div className="min-w-0 flex-1">
          <h4 className="pe-8 text-[15px] font-bold leading-snug text-gray-900">
            {title}
          </h4>
          <p className="mt-1 text-xs leading-relaxed text-gray-600">{desc}</p>

          <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("onboarding.docOrder.requiredDocuments")}
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
            {docs.map((key) => {
              const DocumentIcon = DOC_ICONS[key];
              return (
                <li
                  key={key}
                  className="flex items-center gap-1.5 text-[11px] font-medium text-gray-600"
                >
                  <DocumentIcon
                    size={13}
                    strokeWidth={2}
                    className={active ? "text-[#bf0035]" : "text-gray-400"}
                    aria-hidden
                  />
                  {t(`onboarding.documents.${key}`)}
                </li>
              );
            })}
          </ul>
        </div>

        <span
          aria-hidden
          className={cn(
            "absolute top-4 end-4 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors sm:top-5 sm:end-5",
            active ? "border-[#bf0035]" : "border-gray-300",
          )}
        >
          {active ? (
            <span className="h-2.5 w-2.5 rounded-full bg-[#bf0035]" />
          ) : null}
        </span>
      </div>
    </motion.button>
  );
};

/* ─────────────────────────── Documents-ready confirm ─────────────────────────── */

interface DocumentsReadyConfirmProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const DocumentsReadyConfirm: React.FC<DocumentsReadyConfirmProps> = ({
  checked,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-describedby="documents-ready-hint"
      className={cn(
        "group flex w-full cursor-pointer select-none items-start gap-3 rounded-lg px-1 py-2 text-start transition-colors",
        "hover:bg-gray-50/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0035] focus-visible:ring-offset-2",
      )}
      onClick={() => onChange(!checked)}
    >
      <span
        className={cn(
          "w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ring-1 transition-colors",
          checked
            ? "bg-[#bf0035] ring-[#bf0035] text-white"
            : "bg-white ring-gray-300 text-transparent",
        )}
        aria-hidden="true"
      >
        <Check size={14} strokeWidth={3} />
      </span>
      <span className="min-w-0">
        <span
          className={cn(
            "block text-sm font-semibold leading-snug",
            checked ? "text-[#bf0035]" : "text-gray-900",
          )}
        >
          {t("onboarding.docOrder.checklistConfirm")}
        </span>
        <span
          id="documents-ready-hint"
          className="mt-1 block text-xs leading-relaxed text-gray-500"
        >
          {t("onboarding.docOrder.checklistHint")}
        </span>
      </span>
    </button>
  );
};

export default DocOrderStep;
